import React, { ReactNode, useEffect, useState } from 'react'
import AuthedLayout from '../../Layouts/Authed';
import { Button, Row, Col, Form } from 'react-bootstrap';
import { Link, useForm, usePage } from '@inertiajs/react';
import Valuation from '../../Data/Valuation';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import FormHeader from '../../Components/Forms/FormHeader';
import AgencyManagementSystemSoftware from '../../Data/AgencyManagmentSystemSoftware';
import YesNoSelect from '../../Components/Forms/YesNoSelect';
import { getAllPaperlessAgencyStatuses } from '../../Data/PaperlessAgencyStatus';
import DecimalInput from '../../Components/Forms/DecimalInput';
import Tooltip from '../../Components/Forms/Tooltip';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';
import MarkComplete from '../../Components/Forms/MarkComplete';

interface Props {
	valuation: Valuation;
	agencyManagementSystemSoftware: AgencyManagementSystemSoftware;
	amsSoftwares: AgencyManagementSystemSoftware[];
}

interface FormData {
	has_producer_agreements: boolean;
	has_employment_agreements: boolean;
	paperless_agency: string;
	part_of_agency_network: boolean;
	agency_network_name: string;
	amsSoftware: number | null;
	amsSoftwareNewName: string;
	personal_lines_split: number;
	commercial_lines_split: number;
	health_insurance_split: number;
	life_insurance_split: number;
	medicare_split: number;
	e_and_s_split: number;
	small_commercial_business_percent: number;
	mark_complete: boolean;
}

const AgencyInformation = (props: Props) => {
	const { errors, readOnly } = usePage<any>().props;

	const { data, setData, post, processing } = useForm<FormData>({
		has_producer_agreements: props.valuation.has_producer_agreements ?? '',
		has_employment_agreements: props.valuation.has_employment_agreements ?? '',
		paperless_agency: props.valuation.paperless_agency ?? '',
		part_of_agency_network: props.valuation.part_of_agency_network ?? '',
		agency_network_name: props.valuation.agency_network_name ?? '',
		amsSoftware: props.agencyManagementSystemSoftware
			? props.agencyManagementSystemSoftware.id
			: null,
		amsSoftwareNewName: '',
		personal_lines_split: props.valuation.personal_lines_split ?? '',
		commercial_lines_split: props.valuation.commercial_lines_split ?? '',
		health_insurance_split: props.valuation.health_insurance_split ?? '',
		life_insurance_split: props.valuation.life_insurance_split ?? '',
		medicare_split: props.valuation.medicare_split ?? '',
		e_and_s_split: props.valuation.e_and_s_split ?? '',
		small_commercial_business_percent:
			props.valuation.small_commercial_business_percent ?? '',
		mark_complete: props.valuation.completed_steps?.includes('agency-information') ?? false,
	});
	const autosaveStatus = useValuationStepAutosave('agency-information', props.valuation.id, data);

	const [amsSoftwareOptions, setAmsSoftwareOptions] = useState<
		{ label: string; value: number }[]
	>([]);
	const [amsSoftwareSelect, setAmsSoftwareSelect] = useState<{
		label: string;
		value: number;
	}>();

	const submit = (event: any) => {
		event.preventDefault();
		post(`/valuation-form/${props.valuation.id}/agency-information`);
	};

	const dataSetter = (name: keyof FormData) => (event: any) =>
		setData(name, event.target.value);

	const setPaperlessAgency = (input: any) => {
		setData('paperless_agency', input.value);
	};

	const paperlessOptions = getAllPaperlessAgencyStatuses();

	const setPartOfAgencyNetwork = (input: any) => {
		setData({
			...data,
			part_of_agency_network: input.value,
			agency_network_name: input.value ? data.agency_network_name : '',
		});
	};

	const handleAmsSoftwareSelect = (input: any) => {
		if (input.__isNew__) {
			let newData = {
				...data,
				amsSoftwareNewName: input.label,
				amsSoftware: null,
			};
			setData(newData);
		} else {
			let newData = {
				...data,
				amsSoftwareNewName: '',
				amsSoftware: input.value,
			};
			setData(newData);
		}
	};

	useEffect(() => {
		const softwareOptions = props.amsSoftwares.map(
			(software: AgencyManagementSystemSoftware, index: number) => {
				return { label: software.name, value: software.id };
			}
		);
		setAmsSoftwareOptions(softwareOptions);
	}, [props]);

	useEffect(() => {
		if(data.amsSoftware !== null) {
			props.amsSoftwares.map(
				(software: AgencyManagementSystemSoftware) => {
					if(software.id == data.amsSoftware) {
						setAmsSoftwareSelect({label: software.name, value: software.id});
					}
				}
			);
		}

		scrollTo(0,0);
	}, [])

	return (
		<div>
			<FormHeader
				agencyName={props.valuation.agency_name}
				effectiveDate={props.valuation.effective_date}
				pageName='Agency Information'
			/>
			<Form onSubmit={submit} className='valuationForm'>
				<fieldset disabled={readOnly}>
				<Row className='my-4'>
					<Col md={6}>
						<Form.Group>
							<Form.Label>
								Is your agency paperless?
								<Tooltip id='paperless-agency'>
									If your agency is primarily digital then select 'Yes.'
								</Tooltip>
							</Form.Label>
							{errors['paperless_agency'] && (
								<Form.Control.Feedback type='invalid'>
									{errors['paperless_agency']}
								</Form.Control.Feedback>
							)}
							<ReactSelect
								name='paperless_agency'
								options={paperlessOptions}
								value={
									paperlessOptions.find(
										(option) => option.value === data.paperless_agency,
									) ?? null
								}
								onChange={setPaperlessAgency}
								className='input'
							/>
						</Form.Group>
					</Col>
				</Row>
				<Row className='mt-4'>
					<Col>
						<Form.Label>What Agency Management System do you currently use?</Form.Label>
						<Tooltip id='ams'>
							If you do not see your system then email jarod@ohioinsuranceagents.com the option you would like to see.
						</Tooltip>
					</Col>
				</Row>
				<Row className='mb-4'>
					<Col md={6}>
						<CreatableSelect
							name='agencyManagementSystemSoftware'
							placeholder='Software / System'
							value={amsSoftwareSelect}
							onChange={handleAmsSoftwareSelect}
							options={amsSoftwareOptions}
							className='input'
						/>
					</Col>
				</Row>
				<Row className='my-4'>
					<Col md={6}>
						<YesNoSelect
							name='part_of_agency_network'
							label='Is your business part of an agency network or aggregator?'
							value={data.part_of_agency_network}
							onChange={setPartOfAgencyNetwork}
							errors={errors}
						/>
					</Col>
					{data.part_of_agency_network && (
						<Col md={6}>
							<Form.Group>
								<Form.Label>What is the name of your agency network or aggregator?</Form.Label>
								{errors['agency_network_name'] && (
									<Form.Control.Feedback type='invalid'>
										{errors['agency_network_name']}
									</Form.Control.Feedback>
								)}
								<Form.Control
									name='agency_network_name'
									value={data.agency_network_name}
									onChange={dataSetter('agency_network_name')}
									isInvalid={!!errors['agency_network_name']}
								/>
							</Form.Group>
						</Col>
					)}
				</Row>
				<Row className='mt-4'>
					<Col>
						<Form.Label>What is your agency’s lines of business split?</Form.Label>
						<Tooltip id='lines-split'>
							What percent of your agency's book of business comes from each line of business?
						</Tooltip>
					</Col>
				</Row>
				<Row className='mb-4'>
					<Col md={6}>
						<Form.Label>Personal Lines:</Form.Label>
						<DecimalInput
							name='personal_lines_split'
							value={data.personal_lines_split}
							onChange={dataSetter('personal_lines_split')}
							errors={errors}
							prefix={null}
							postfix='%'
							arrayName='personal_lines_split'
							errorArrayName='personal_lines_split'
							overrideArrayName={true}
							overrideErrorArrayName={true}
						/>
					</Col>
					<Col md={6}>
						<Form.Label>Commercial Lines:</Form.Label>
						<DecimalInput
							name='commercial_lines_split'
							value={data.commercial_lines_split}
							onChange={dataSetter('commercial_lines_split')}
							errors={errors}
							prefix={null}
							postfix='%'
							arrayName='commercial_lines_split'
							errorArrayName='commercial_lines_split'
							overrideArrayName={true}
							overrideErrorArrayName={true}
						/>
					</Col>
				</Row>
				<Row className='mb-4'>
					<Col md={6}>
						<Form.Label>Health Insurance:</Form.Label>
						<DecimalInput
							name='health_insurance_split'
							value={data.health_insurance_split}
							onChange={dataSetter('health_insurance_split')}
							errors={errors}
							prefix={null}
							postfix='%'
							arrayName='health_insurance_split'
							errorArrayName='health_insurance_split'
							overrideArrayName={true}
							overrideErrorArrayName={true}
						/>
					</Col>
					<Col md={6}>
						<Form.Label>Life Insurance:</Form.Label>
						<DecimalInput
							name='life_insurance_split'
							value={data.life_insurance_split}
							onChange={dataSetter('life_insurance_split')}
							errors={errors}
							prefix={null}
							postfix='%'
							arrayName='life_insurance_split'
							errorArrayName='life_insurance_split'
							overrideArrayName={true}
							overrideErrorArrayName={true}
						/>
					</Col>
				</Row>
				<Row className='mb-4'>
					<Col md={6}>
						<Form.Label>Medicare:</Form.Label>
						<DecimalInput
							name='medicare_split'
							value={data.medicare_split}
							onChange={dataSetter('medicare_split')}
							errors={errors}
							prefix={null}
							postfix='%'
							arrayName='medicare_split'
							errorArrayName='medicare_split'
							overrideArrayName={true}
							overrideErrorArrayName={true}
						/>
					</Col>
					<Col md={6}>
						<Form.Label>E&amp;S:</Form.Label>
						<DecimalInput
							name='e_and_s_split'
							value={data.e_and_s_split}
							onChange={dataSetter('e_and_s_split')}
							errors={errors}
							prefix={null}
							postfix='%'
							arrayName='e_and_s_split'
							errorArrayName='e_and_s_split'
							overrideArrayName={true}
							overrideErrorArrayName={true}
						/>
					</Col>
				</Row>
				<Row className='my-4'>
					<Col md={6}>
						<Form.Label>Of your commercial lines, what percentage is small commercial business?</Form.Label>
						<Tooltip id='small-commercial'>
							We define small commercial business as $10k in premium or less.
						</Tooltip>
						<DecimalInput
							name='small_commercial_business_percent'
							value={data.small_commercial_business_percent}
							onChange={dataSetter(
								'small_commercial_business_percent'
							)}
							errors={errors}
							prefix={null}
							postfix='%'
							arrayName='small_commercial_business_percent'
							errorArrayName='small_commercial_business_percent'
							overrideArrayName={true}
							overrideErrorArrayName={true}
						/>
					</Col>
				</Row>
				</fieldset>
				<div className='d-flex justify-content-between align-items-center'>
					<Button
						as={Link as any}
						href={`/valuation-form/${props.valuation.id}/non-recurring-expenses`}
						variant='outline-primary'
						className='mt-4'
					>
						Back
					</Button>
					<div className='d-flex align-items-center'>
						{!readOnly && <AutosaveStatus status={autosaveStatus} />}
						{!readOnly && (
							<MarkComplete
								checked={data.mark_complete}
								onChange={(checked) => setData('mark_complete', checked)}
								className='mt-4 ms-4'
							/>
						)}
						{readOnly ? (
							<Button
								as={Link as any}
								href={`/valuation-form/${props.valuation.id}/carrier-loss-ratios`}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						) : (
							<Button
								type='submit'
								disabled={processing}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						)}
					</div>
				</div>
			</Form>
		</div>
	);
};

AgencyInformation.layout = (page: ReactNode) => (
	<AuthedLayout>{page}</AuthedLayout>
);

export default AgencyInformation;
